/**
 * BookGuard book repair hub + segment pages: starter bundles, product grid, width chooser,
 * mini cart and quote modal. Vanilla JS, no dependencies. Adapted (copied) from bg-hymnal-planner.js.
 * Prices come only from the #br-data JSON blob that snippets/bg-repair-data.liquid renders from
 * all_products[...]; nothing here hardcodes a price. List price only: no discount codes are applied.
 *
 * The pure core (BRCore) has no DOM access so it can be tested in node:
 *   const BRCore = require('./bg-repair.js');
 */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Core                                                                */
  /* ------------------------------------------------------------------ */

  var ROLL_IN = 540;                       // 15 yd roll
  var WIDTHS = [1.5, 2, 3, 4];             // stock cloth widths used by the chooser (in)
  var WIDTH_ROLE = { '1.5': '15', '2': '2', '3': '3', '4': '4' };
  /** Tape-only colors and the nearest wings/corners cloth. Yellow has no close match. */
  var TAPE_ONLY_MATCH = { 'Tan': 'Brown', 'Olive': 'Forest Green', 'Gray': 'Black', 'Yellow': 'White' };

  /**
   * Rounds to 3 decimals to keep float noise out of ceil/floor.
   * @param {number} n
   * @returns {number}
   */
  function r3(n) { return Math.round(n * 1000) / 1000; }

  /**
   * Formats inches without trailing zeros.
   * @param {number} n
   * @returns {string}
   */
  function fmtIn(n) { return String(Math.round(n * 100) / 100); }

  /**
   * Label for a stock tape width.
   * @param {number} w - inches
   * @returns {string}
   */
  function widthLabel(w) { return w === 1.5 ? '1-1/2"' : w === 0.75 ? '3/4"' : String(w) + '"'; }

  /**
   * Formats integer cents as money.
   * @param {number} cents
   * @param {string} [currency]
   * @returns {string}
   */
  function fmtMoney(cents, currency) {
    var v = (cents || 0) / 100;
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(v); }
    catch (e) { return '$' + v.toFixed(2); }
  }

  /**
   * Finds a variant by exact title, then case-insensitively.
   * @param {object} product - {variants:[{id,title,price}]}
   * @param {string} title
   * @returns {object|null}
   */
  function findVariant(product, title) {
    if (!product || !product.variants) return null;
    var t = String(title).toLowerCase();
    for (var i = 0; i < product.variants.length; i++) if (product.variants[i].title === title) return product.variants[i];
    for (var j = 0; j < product.variants.length; j++) if (String(product.variants[j].title).toLowerCase() === t) return product.variants[j];
    return null;
  }

  /**
   * Wings/corners color for a chosen color (tape-only colors fall back to the closest cloth).
   * @param {string} color
   * @returns {string}
   */
  function accessoryColor(color) { return TAPE_ONLY_MATCH[color] || color; }

  /**
   * Resolves the variant for a role + color.
   * Cloth tape titles: "Navy Blue Cloth"; wings/corners: "Premium Cloth - Navy Blue" (corners call Forest Green "Green");
   * single-variant products (clear, vinyl, 30-yd rolls, Gold-Book, kit) ignore color.
   * @param {object} data - #br-data blob
   * @param {string} role - e.g. cloth3, wings, corners, clear15
   * @param {?string} color
   * @returns {object|null} variant
   */
  function variantFor(data, role, color) {
    var p = data.products[role];
    if (!p || !p.variants || !p.variants.length) return null;
    if (p.variants.length === 1) return p.variants[0];
    if (/^cloth/.test(role)) return findVariant(p, color + ' Cloth');
    var c = accessoryColor(color);
    if (role === 'corners') return findVariant(p, 'Premium Cloth - ' + (c === 'Forest Green' ? 'Green' : c));
    if (role === 'wings') return findVariant(p, 'Premium Cloth - ' + c);
    return p.variants[0];
  }

  /**
   * Parses a bundle line spec "role|qty|color;..." (role may contain @ = width code).
   * @param {string} spec
   * @returns {Array<{role:string, qty:number, color:string}>}
   */
  function parseLines(spec) {
    return String(spec || '').split(';').filter(Boolean).map(function (s) {
      var f = s.split('|');
      return { role: f[0], qty: Number(f[1]) || 0, color: f[2] || '-' };
    });
  }

  /**
   * Prices a starter bundle at list price.
   * @param {object} data
   * @param {Array} lines - parseLines() output
   * @param {string} widthCode - replaces @ in roles (e.g. "15", "2", "3")
   * @param {?string} color - replaces C
   * @returns {{ok:boolean, items:Array<{id:number,quantity:number}>, totalCents:number, freeShip:boolean, missing:Array<string>}}
   */
  function bundleQuote(data, lines, widthCode, color) {
    var items = [], total = 0, missing = [];
    lines.forEach(function (ln) {
      var role = ln.role.replace('@', widthCode || '');
      var col = ln.color === 'C' ? color : (ln.color === '-' ? null : ln.color);
      var v = variantFor(data, role, col);
      if (!v) { missing.push(role + (col ? ' ' + col : '')); return; }
      items.push({ id: v.id, quantity: ln.qty });
      total += v.price * ln.qty;
    });
    return { ok: !missing.length && items.length > 0, items: items, totalCents: total, freeShip: total >= (data.freeShipCents || 15000), missing: missing };
  }

  /**
   * Width rule (sizing.md): tape = smallest stock width >= spine + 1" (1/2" onto each cover).
   * Length rule: strip = book height + 1.5" (3/4" turned in at head and tail); 540" per 15-yd roll.
   * @param {number} spine - inches
   * @param {?number} height - inches (optional)
   * @returns {{ok:boolean, need:number, width:number, over:boolean, strip:?number, perRoll:?number}}
   */
  function chooseWidth(spine, height) {
    var s = Number(spine);
    if (!(s > 0)) return { ok: false };
    var need = r3(s + 1);
    var width = 4, over = true;
    for (var i = 0; i < WIDTHS.length; i++) {
      if (need <= WIDTHS[i] + 1e-9) { width = WIDTHS[i]; over = false; break; }
    }
    var h = Number(height);
    var strip = h > 0 ? r3(h + 1.5) : null;
    return { ok: true, need: need, width: width, over: over, strip: strip, perRoll: strip ? Math.floor(r3(ROLL_IN / strip)) : null };
  }

  var Core = {
    ROLL_IN: ROLL_IN, WIDTHS: WIDTHS, WIDTH_ROLE: WIDTH_ROLE, TAPE_ONLY_MATCH: TAPE_ONLY_MATCH,
    fmtMoney: fmtMoney, fmtIn: fmtIn, widthLabel: widthLabel, findVariant: findVariant, accessoryColor: accessoryColor,
    variantFor: variantFor, parseLines: parseLines, bundleQuote: bundleQuote, chooseWidth: chooseWidth
  };
  if (typeof module === 'object' && module.exports) module.exports = Core;
  root.BRCore = Core;
  if (typeof document === 'undefined') return;

  /* ------------------------------------------------------------------ */
  /* UI                                                                  */
  /* ------------------------------------------------------------------ */

  var DATA;
  try { DATA = JSON.parse(document.getElementById('br-data').textContent); } catch (e) { return; }
  var money = function (c) { return fmtMoney(c, DATA.currency); };
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /**
   * Sends an analytics event to gtag (or dataLayer).
   * @param {string} name
   * @param {object} params
   */
  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
      else if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(Object.assign({ event: name }, params || {}));
    } catch (e) {}
  }

  /**
   * Escapes text for HTML insertion.
   * @param {*} s
   * @returns {string}
   */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- swatch paint (same values as the hymnal page) ---------- */
  var SWATCH = {
    'Burgundy': '#6B1E2A', 'Red': '#A3262A', 'Navy Blue': '#1F2A44', 'Black': '#1F1C1B', 'Forest Green': '#24452F',
    'Brown': '#5B3B26', 'White': '#F6F2EA', 'Tan': '#C4A67F', 'Olive': '#6A6939', 'Gray': '#85847F', 'Yellow': '#E2B93B'
  };
  $$('.br-swatch[data-color]').forEach(function (el) { el.style.backgroundColor = SWATCH[el.getAttribute('data-color')] || '#999'; });

  /* ---------- cart ---------- */

  /**
   * POST JSON helper that rejects on non-2xx with Shopify's error description.
   * @param {string} url
   * @param {object} body
   * @returns {Promise<object>}
   */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || ('HTTP ' + r.status));
        return j;
      });
    });
  }

  /** @returns {Promise<object>} /cart.js */
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  /**
   * Adds items, tags the cart with bg_* attributes (Shopify merges attributes, so the
   * first-touch tracker's keys are kept), then opens the drawer.
   * @param {Array<{id:number,quantity:number}>} items
   * @param {object} attrs
   * @param {HTMLElement} btn
   * @param {?HTMLElement} msgEl
   * @returns {Promise<void>}
   */
  function addToCart(items, attrs, btn, msgEl) {
    items = items.filter(function (it) { return it.id && it.quantity > 0; });
    if (!items.length) return Promise.resolve();
    var label = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    if (msgEl) { msgEl.textContent = ''; msgEl.classList.remove('is-err'); }
    var addedLines = null;
    return postJSON('/cart/add.js', { items: items })
      .then(function (addResponse) {
        // Capture the real added line(s) (each has a unique `key`, not just
        // a variant id) so the Klaviyo listener can match the exact cart
        // line even if this variant already existed in the cart with
        // different properties.
        addedLines = (addResponse && addResponse.items) || items;
        return postJSON('/cart/update.js', { attributes: attrs }).catch(function () {});
      })
      .then(getCart)
      .then(function (cart) {
        // Klaviyo ATC tracking (snippets/cl-klaviyo-atc.liquid) listens for this
        // event; this add path bypasses gem-ajax-cart.js/cl-spec.js entirely, so
        // without this dispatch these adds were invisible to Klaviyo. Fired
        // after the cart attributes/totals are settled (consistent with
        // bg-hymnal-planner.js, which must wait for its volume discount sync).
        addedLines.forEach(function (it, idx) {
          // it.quantity is the cart line's RESULTING total quantity (Shopify
          // merges into an existing line of the same variant/properties), not
          // the delta just added - use the originally requested quantity for
          // this request slot instead, so AddedItemQuantity reflects "what the
          // customer just did", not "what's on the line now".
          var requestedQty = (items[idx] && items[idx].quantity) || it.quantity;
          document.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { variant: it, quantity: requestedQty }
          }));
        });
        openDrawer(cart);
      })
      .catch(function () {
        if (msgEl) {
          msgEl.textContent = 'Sorry, that didn’t go through. Please try again, or call us and we’ll place the order for you.';
          msgEl.classList.add('is-err');
        }
      })
      .then(function () { btn.disabled = false; btn.textContent = label; });
  }

  /* ---------- overlays ---------- */
  var lastOpener = null;

  function openOverlay(el, focusSel) {
    lastOpener = document.activeElement;
    el.hidden = false;
    document.documentElement.classList.add('br-lock');
    var f = focusSel ? $(focusSel, el) : $('[data-br-close]', el);
    if (f) setTimeout(function () { f.focus(); }, 30);
  }

  function closeOverlay(el) {
    if (!el || el.hidden) return;
    el.hidden = true;
    document.documentElement.classList.remove('br-lock');
    if (lastOpener && lastOpener.focus) lastOpener.focus();
  }

  $$('.br-overlay').forEach(function (ov) {
    ov.addEventListener('mousedown', function (e) { if (e.target === ov) closeOverlay(ov); });
    $$('[data-br-close]', ov).forEach(function (b) { b.addEventListener('click', function () { closeOverlay(ov); }); });
  });

  document.addEventListener('keydown', function (e) {
    var open = $$('.br-overlay').filter(function (o) { return !o.hidden; })[0];
    if (!open) return;
    if (e.key === 'Escape') { closeOverlay(open); return; }
    if (e.key === 'Tab') {
      var f = $$('a[href], button:not([disabled]), input:not([tabindex="-1"]):not([type="hidden"]), textarea, select', open)
        .filter(function (x) { return x.offsetParent !== null; });
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  });

  /**
   * Renders the mini cart from /cart.js and opens it.
   * @param {object} cart
   */
  function openDrawer(cart) {
    var drawer = $('#br-drawer');
    if (!drawer) { location.href = '/cart'; return; }
    $('#br-drawer-items').innerHTML = (cart.items || []).map(function (it) {
      var img = it.image ? '<img src="' + esc(it.image + (it.image.indexOf('?') > -1 ? '&' : '?') + 'width=120') + '" width="56" height="56" alt="" loading="lazy">' : '<span class="br-drawer-ph"></span>';
      return '<li>' + img + '<span class="br-drawer-t"><strong>' + esc(it.product_title) + '</strong>' +
        (it.variant_title ? '<span>' + esc(it.variant_title) + '</span>' : '') +
        '<span>Qty ' + it.quantity + '</span></span><span class="br-drawer-p">' + money(it.original_line_price != null ? it.original_line_price : it.final_line_price) + '</span></li>';
    }).join('');
    var disc = Number(cart.total_discount) || 0;
    var discRow = $('#br-drawer-disc');
    discRow.hidden = !(disc > 0);
    var codes = (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
    $('#br-drawer-disc-label').textContent = 'Discount' + (codes.length ? ' (' + codes.join(', ') + ')' : '');
    $('#br-drawer-disc-val').textContent = '−' + money(disc);
    var sub = cart.total_price != null ? cart.total_price : cart.items_subtotal_price;
    $('#br-drawer-subtotal').textContent = money(sub);
    var free = DATA.freeShipCents || 15000;
    $('#br-drawer-ship').textContent = sub >= free ? 'Your order ships free.' : 'Add ' + money(free - sub) + ' more for free shipping.';
    openOverlay(drawer, '#br-drawer-checkout');
  }

  /* ---------- width switch (bundles) ---------- */
  var pageWidth = (function () {
    var on = $('.br-width-switch .br-seg[aria-pressed="true"]');
    return on ? on.getAttribute('data-wcode') : '3';
  })();
  var widthText = function () {
    var on = $('.br-width-switch .br-seg[aria-pressed="true"]');
    return on ? on.getAttribute('data-wlabel') : '';
  };

  /* ---------- bundles ---------- */

  /**
   * Re-prices one bundle card for its color and the page width.
   * @param {HTMLElement} card
   * @returns {?{quote:object, color:?string, name:string}}
   */
  function renderBundle(card) {
    var sel = $('.br-bundle-color', card);
    var color = sel ? sel.value : null;
    var q = bundleQuote(DATA, parseLines(card.getAttribute('data-lines')), pageWidth, color);
    var btn = $('.br-bundle-add', card);
    btn.disabled = !q.ok;
    if (!q.ok) return null;
    $('.br-pack-price', card).textContent = money(q.totalCents);
    var perN = Number(card.getAttribute('data-per-n')) || 0;
    var per = $('.br-pack-per-val', card);
    if (per && perN) per.textContent = money(Math.round(q.totalCents / perN));
    $('.br-pack-ship', card).classList.toggle('is-off', !q.freeShip);
    var wt = widthText();
    if (wt) $$('.br-w', card).forEach(function (s) { s.textContent = wt; });
    return { quote: q, color: color, name: card.getAttribute('data-name') };
  }

  var bundles = $$('.br-bundle');
  bundles.forEach(function (card) {
    renderBundle(card);
    var sel = $('.br-bundle-color', card);
    if (sel) sel.addEventListener('change', function () { renderBundle(card); });
    $('.br-bundle-add', card).addEventListener('click', function () {
      var r = renderBundle(card);
      if (!r) return;
      var hasW = /@/.test(card.getAttribute('data-lines'));
      var tag = r.name + (hasW ? ' - ' + widthText().replace(/"/g, 'in') : '') + (r.color ? ' - ' + r.color : '');
      track('br_bundle_add', { bundle: r.name, color: r.color || '', value: r.quote.totalCents / 100, currency: DATA.currency, source: DATA.source });
      addToCart(r.quote.items, { bg_source: DATA.source, bg_pack: tag }, this, $('.br-bundle-msg', card));
    });
  });
  $$('.br-width-switch .br-seg').forEach(function (b) {
    b.addEventListener('click', function () {
      pageWidth = b.getAttribute('data-wcode');
      $$('.br-width-switch .br-seg').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
      bundles.forEach(renderBundle);
    });
  });

  /* ---------- product grid ---------- */

  /**
   * Re-prices one product card for its width (role) and color.
   * @param {HTMLElement} card
   * @returns {?{variant:object, role:string, color:?string}}
   */
  function renderProd(card) {
    var role = $('.br-prod-role', card).value;
    var csel = $('.br-prod-color', card);
    var color = csel ? csel.value : null;
    var v = variantFor(DATA, role, color);
    var btn = $('.br-prod-add', card);
    btn.disabled = !v;
    if (!v) return null;
    $('.br-prod-price-val', card).textContent = money(v.price);
    var p = DATA.products[role];
    if (p && p.url) $$('.br-prod-link', card).forEach(function (a) { a.href = p.url + (p.variants.length > 1 ? '?variant=' + v.id : ''); });
    return { variant: v, role: role, color: color };
  }

  /**
   * Marks a product card that would sit alone on the last grid row so CSS can stretch it across the row.
   * Columns are read from the live grid, so it follows every breakpoint.
   */
  function layoutProdGrids() {
    $$('.br-prod-grid').forEach(function (g) {
      var cards = $$('.br-prod', g);
      cards.forEach(function (c) { c.classList.remove('br-prod-wide'); });
      var cols = getComputedStyle(g).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (cols > 1 && cards.length % cols === 1) cards[cards.length - 1].classList.add('br-prod-wide');
    });
  }
  layoutProdGrids();
  var lgTimer;
  window.addEventListener('resize', function () { clearTimeout(lgTimer); lgTimer = setTimeout(layoutProdGrids, 120); });

  $$('.br-prod').forEach(function (card) {
    renderProd(card);
    $$('select', card).forEach(function (s) { s.addEventListener('change', function () { renderProd(card); }); });
    $('.br-prod-add', card).addEventListener('click', function () {
      var r = renderProd(card);
      if (!r) return;
      track('br_product_add', { role: r.role, color: r.color || '', value: r.variant.price / 100, currency: DATA.currency, source: DATA.source });
      addToCart([{ id: r.variant.id, quantity: 1 }], { bg_source: DATA.source }, this, $('.br-prod-msg', card));
    });
  });

  /* ---------- width chooser ---------- */
  var ch = $('#br-chooser');
  if (ch) {
    var spineEl = $('#br-ch-spine', ch), heightEl = $('#br-ch-height', ch), colorEl = $('#br-ch-color', ch);
    var outW = $('#br-ch-w', ch), outWhy = $('#br-ch-why', ch), outList = $('#br-ch-list', ch);
    var addBtn = $('#br-ch-add', ch), clearBtn = $('#br-ch-clear', ch), quoteBtn = $('#br-ch-quote', ch), msg = $('#br-ch-msg', ch);
    var curr = null;
    var updateChooser = function () {
      var r = chooseWidth(spineEl.value, heightEl.value);
      curr = r;
      if (!r.ok) {
        outW.textContent = '—';
        outWhy.textContent = 'Enter the spine thickness to see your width.';
        outList.innerHTML = '';
        addBtn.hidden = clearBtn.hidden = true;
        if (quoteBtn) quoteBtn.hidden = true;
        return;
      }
      outW.textContent = widthLabel(r.width);
      outWhy.textContent = 'Spine ' + fmtIn(Number(spineEl.value)) + '" + 1" = ' + fmtIn(r.need) + '" → ' + widthLabel(r.width) + ' tape' +
        (r.over ? ' (our widest stock width; your spine needs more).' : ', so it wraps about 1/2" onto each cover.');
      var li = [];
      if (r.strip) li.push('Cut each strip ' + fmtIn(r.strip) + '" long: the book’s height + 1-1/2" to fold inside at the top and bottom.');
      if (r.perRoll) li.push('One 15-yard roll (540") covers about ' + r.perRoll + ' spines this tall.');
      if (r.over) li.push('Spines over 3" need wider tape than we stock. Ask us to cut BookGuard to fit.');
      outList.innerHTML = li.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');
      var color = colorEl ? colorEl.value : 'Black';
      var cv = variantFor(DATA, 'cloth' + WIDTH_ROLE[String(r.width)], color);
      var xv = variantFor(DATA, 'clear' + WIDTH_ROLE[String(r.width)], null);
      addBtn.hidden = !cv;
      if (cv) addBtn.textContent = 'Add ' + widthLabel(r.width) + ' ' + color + ' cloth tape ·\u00a0' + money(cv.price);
      clearBtn.hidden = !xv;
      if (xv) clearBtn.textContent = 'Or ' + widthLabel(r.width) + ' clear tape ·\u00a0' + money(xv.price);
      if (quoteBtn) quoteBtn.hidden = !r.over;
    };
    [spineEl, heightEl, colorEl].forEach(function (el) { if (el) { el.addEventListener('input', updateChooser); el.addEventListener('change', updateChooser); } });
    addBtn.addEventListener('click', function () {
      if (!curr || !curr.ok) return;
      var color = colorEl ? colorEl.value : 'Black';
      var v = variantFor(DATA, 'cloth' + WIDTH_ROLE[String(curr.width)], color);
      if (!v) return;
      track('br_chooser_add', { width: curr.width, color: color, kind: 'cloth', source: DATA.source });
      addToCart([{ id: v.id, quantity: 1 }], { bg_source: DATA.source }, this, msg);
    });
    clearBtn.addEventListener('click', function () {
      if (!curr || !curr.ok) return;
      var v = variantFor(DATA, 'clear' + WIDTH_ROLE[String(curr.width)], null);
      if (!v) return;
      track('br_chooser_add', { width: curr.width, kind: 'clear', source: DATA.source });
      addToCart([{ id: v.id, quantity: 1 }], { bg_source: DATA.source }, this, msg);
    });
    updateChooser();
  }

  /* ---------- quote: modal + inline forms ---------- */
  var quote = $('#br-quote');

  /** vid/sy exactly as snippets/cl-quote-form.liquid reads them. */
  function getAttribution() {
    var vid = '', sy = '';
    try { if (window._clAttribution) { vid = window._clAttribution.visitorId || ''; sy = window._clAttribution.shopifyY || ''; } } catch (e) {}
    if (!sy) { try { var m = document.cookie.match(/(?:^|; )_shopify_y=([^;]*)/); if (m) sy = decodeURIComponent(m[1]); } catch (e) {} }
    return { vid: vid, sy: sy };
  }

  $$('[data-br-quote]').forEach(function (b) {
    b.addEventListener('click', function (e) {
      if (!quote) return;
      e.preventDefault();
      var ctx = b.getAttribute('data-br-quote') || '';
      quote.setAttribute('data-context', ctx);
      if (curr && curr.ok && b.id === 'br-ch-quote') {
        quote.setAttribute('data-context', ctx + ' Spine ' + fmtIn(Number(spineEl.value)) + 'in' + (heightEl.value ? ', height ' + fmtIn(Number(heightEl.value)) + 'in' : '') + '.');
      }
      var form = $('.br-quote-form', quote);
      $('.br-quote-status', form).textContent = '';
      var sb = $('.br-quote-submit', form); sb.disabled = false; sb.textContent = 'Send my request';
      openOverlay(quote, '#br-q-name');
    });
  });

  $$('.br-quote-form').forEach(function (form) {
    var status = $('.br-quote-status', form);
    var submit = $('.br-quote-submit', form);
    var val = function (n) { var el = form.elements[n]; return el ? String(el.value || '').trim() : ''; };
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var name = val('name'), email = val('email');
      if (!name || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        status.className = 'br-msg br-quote-status is-err';
        status.textContent = 'Please add your name and a valid email.';
        return;
      }
      if (val('hp')) { status.className = 'br-msg br-quote-status is-ok'; status.textContent = 'Thank you.'; return; }
      var a = getAttribution();
      var wrap = form.closest('[data-quote-title]');
      var ctx = (form.closest('#br-quote') ? quote.getAttribute('data-context') : form.getAttribute('data-context')) || '';
      var po = form.elements.po && form.elements.po.checked ? ' Will pay by purchase order.' : '';
      var payload = {
        vid: a.vid, sy: a.sy, name: name, email: email,
        company: val('company'), phone: val('phone'),
        product_title: (wrap && wrap.getAttribute('data-quote-title')) || 'BookGuard book repair quote',
        product_url: location.href,
        quantity: val('quantity'),
        message: (ctx ? ctx + '\n\n' : '') + val('note') + po,
        heard_about: DATA.source, hp: ''
      };
      submit.disabled = true; submit.textContent = 'Sending…';
      fetch('https://forms.chromalabel.com/v1/lead', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        track('br_quote_submit', { source: DATA.source, quantity: payload.quantity });
        status.className = 'br-msg br-quote-status is-ok';
        status.textContent = 'Thank you. We’ll look it over and reply by email.';
        submit.textContent = 'Sent';
      }).catch(function () {
        status.className = 'br-msg br-quote-status is-err';
        status.textContent = 'Something went wrong. Please email support@chromalabel.com and we will help.';
        submit.disabled = false; submit.textContent = 'Send my request';
      });
    });
  });

  /* ---------- videos: autoplay in view, never with reduced motion ---------- */
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  $$('[data-br-video]').forEach(function (wrap) {
    var v = $('video', wrap);
    var btn = $('.br-video-play', wrap);
    v.muted = true;
    var sync = function () { wrap.classList.toggle('is-playing', !v.paused); };
    v.addEventListener('play', sync); v.addEventListener('pause', sync);
    btn.addEventListener('click', function () {
      if (v.paused) { v.play().catch(function () {}); wrap.dataset.user = 'play'; }
      else { v.pause(); wrap.dataset.user = 'pause'; }
    });
    if (reduce || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (ents) {
      ents.forEach(function (en) {
        if (en.isIntersecting) {
          if (v.preload === 'none') v.preload = 'metadata';
          if (wrap.dataset.user !== 'pause') v.play().catch(function () {});
        } else if (!v.paused) { v.pause(); }
      });
    }, { rootMargin: '200px 0px', threshold: 0.25 }).observe(wrap);
  });

  /* ---------- sticky mobile bar: after the hero, hidden while the target section is on screen ---------- */
  var sticky = $('#br-sticky');
  var hero = $('#br-hero');
  if (sticky && hero && 'IntersectionObserver' in window) {
    var target = $(($('a', sticky).getAttribute('href') || '#').replace(/^.*#/, '#'));
    var heroIn = true, targetIn = false;
    var setSticky = function () {
      var show = !heroIn && !targetIn;
      sticky.classList.toggle('is-on', show);
      sticky.setAttribute('aria-hidden', String(!show));
      $('a', sticky).tabIndex = show ? 0 : -1;
    };
    new IntersectionObserver(function (e) { heroIn = e[0].isIntersecting; setSticky(); }).observe(hero);
    if (target) new IntersectionObserver(function (e) { targetIn = e[0].isIntersecting; setSticky(); }).observe(target);
  }
})(typeof window !== 'undefined' ? window : globalThis);
